// src/components/LivePreview.js
import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { Box, useMediaQuery, useTheme, Typography, Fade, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight, TouchApp } from '@mui/icons-material';
import { FormContext } from '../../context/FormContext';
import { PaginationContext } from '../../context/PaginationContext';
import { useLayout } from '../../context/LayoutContext';
import { useDesign } from '../../context/DesignContext';
import { useTypography } from '../../context/TypographyContext';
import { useStyle } from '../../context/StyleContext';
import { useBackground } from '../../context/BackgroundContext';
import { useBarStyle } from '../../context/BarStyleContext';
import LayoutResolver from '../../layouts/LayoutResolver';
import PageControls from './PageControls';
import ClearAllData from './ClearAllDataButton';
import theme, { tokens } from '../../theme/index';
import { useLanguage } from '../../localization';
// Import the empty data structure
import { EMPTY_FORM_DATA } from '../../services/DataService';

// A4 constants
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const A4_RATIO = A4_HEIGHT_MM / A4_WIDTH_MM; // 1.414
const MM_TO_PX_FACTOR = 3.78; // Approximate conversion factor
const PAGE_PADDING_MM = 20; // Standard padding used in layouts
const HEIGHT_BUFFER_PX = -20; // REDUCED buffer to make page break slightly earlier
const TOPBAR_HEIGHT_MM = 71; // Define topbar height - ADJUSTED FROM 50

// Zoom constants
const MOBILE_DEFAULT_SCALE = 0.5;  // 50% for mobile
const DESKTOP_DEFAULT_SCALE = 0.6; // 60% for desktop
const MIN_SCALE = 0.2;             // 20% minimum zoom
const MAX_SCALE = 1.5;             // 150% maximum zoom

function LivePreview({ 
  selectedSections, 
  setSelectedSections, 
  showSettingsInEditor, 
  setShowSettingsInEditor, 
  handleClearAll,
  selectedSpecialEffectId,
}) {
  const { formData, updateFormData } = useContext(FormContext);
  const { pages, setPages, currentPage, setCurrentPage } = useContext(PaginationContext);
  const { selectedLayout } = useLayout();
  const {
    activeDesign,
    currentColors,
  } = useDesign();
  const { typographySettings } = useTypography();
  const { activeStyle: contextActiveStyle } = useStyle();
  const { 
    selectedBackgroundId,
    activeBackgroundComponent: ActiveBackgroundComponent
  } = useBackground();
  const { activeBarStyle } = useBarStyle();
  const { t } = useLanguage();
  
  const contentRef = useRef(null);
  const previewContainerRef = useRef(null);
  const a4ContainerRef = useRef(null);
  const isInitialMount = useRef(true);
  const [isCalculating, setIsCalculating] = useState(false);
  const isDragging = useRef(false);
  const lastPosition = useRef({ x: 0, y: 0 });
  const lastPinchDistance = useRef(null);
  const pinchStartScale = useRef(0);
  const pinchMidpoint = useRef({ x: 0, y: 0 });
  
  // Add media queries for responsive behavior
  const isNarrowScreen = useMediaQuery('(max-width:600px)');
  const isMediumScreen = useMediaQuery('(max-width:900px)');
  
  // Use the appropriate default scale based on screen size
  const defaultScale = isNarrowScreen ? MOBILE_DEFAULT_SCALE : DESKTOP_DEFAULT_SCALE;
  
  // Initialize scale with the default value
  const [scale, setScale] = useState(defaultScale);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const theme = useTheme();

  // Mobile gesture hint states
  const [showGestureHint, setShowGestureHint] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Add an initialization effect that runs once
  useEffect(() => {
    // Explicitly set the initial scale to the default value
    setScale(defaultScale);
  }, [defaultScale]);

  // Show gesture hint on mobile after a delay
  useEffect(() => {
    if (isNarrowScreen && !hasInteracted) {
      const timer = setTimeout(() => {
        setShowGestureHint(true);
        // Keep hint visible for much longer (12 seconds) to make it more permanent
        setTimeout(() => setShowGestureHint(false), 12000);
      }, 800);
      
      return () => clearTimeout(timer);
    }
  }, [isNarrowScreen, hasInteracted]);

  // Update scale adjustment logic
  useEffect(() => {
    const adjustScaleToFit = () => {
      if (!previewContainerRef.current) return;
      
      const container = previewContainerRef.current;
      
      // Get available space (accounting for padding)
      const availableWidth = container.clientWidth - 40;
      const availableHeight = container.clientHeight - 40;
      
      // Calculate scale based on available space while maintaining A4 ratio
      let newScale;
      
      // If container is narrower proportionally than A4
      if (availableWidth / availableHeight < 1 / A4_RATIO) {
        // Width constrained - scale based on width
        newScale = availableWidth / A4_WIDTH_MM;
      } else {
        // Height constrained - scale based on height
        newScale = availableHeight / A4_HEIGHT_MM;
      }
      
      // Apply minimum scale to ensure visibility
      newScale = Math.max(newScale, MOBILE_DEFAULT_SCALE);
      
      // Only auto-scale if it would result in a smaller scale than default
      // This ensures our default scale is respected on initial load
      if (newScale < defaultScale) {
        // Update scale state
        setScale(newScale);
        
        // Reset position when auto-scaling
        setPosition({ x: 0, y: 0 });
      } else {
        // Use the default scale instead
        setScale(defaultScale);
        setPosition({ x: 0, y: 0 });
      }
    };
    
    // Delay the auto-scaling to let initial render complete with default scale
    const timerId = setTimeout(adjustScaleToFit, 100);
    
    // Add window resize listener
    window.addEventListener('resize', adjustScaleToFit);
    
    return () => {
      clearTimeout(timerId);
      window.removeEventListener('resize', adjustScaleToFit);
    };
  }, [isNarrowScreen, isMediumScreen, defaultScale]);

  // Pagination logic - calculate content section heights and create pages
  const calculatePages = useCallback(async () => {
    console.log('calculatePages called:', {
      isCalculating,
      hasContentRef: !!contentRef.current,
      selectedSectionsLength: selectedSections.length,
      hasA4ContainerRef: !!a4ContainerRef.current,
      isNarrowScreen
    });
    
    if (isCalculating || !contentRef.current || selectedSections.length === 0 || !a4ContainerRef.current) {
      console.log('calculatePages early return due to conditions');
      return;
    }
    setIsCalculating(true);

    try {
      const container = contentRef.current;
      const a4Container = a4ContainerRef.current;
      const sectionElements = container.querySelectorAll('[data-section]');
      
      console.log('calculatePages DOM check:', {
        containerChildren: container.children.length,
        sectionElementsLength: sectionElements.length,
        selectedSectionsLength: selectedSections.length
      });
      
      if (!sectionElements.length && selectedSections.length > 0) {
        console.error('calculatePages Error: No elements with [data-section] found.', {
          containerHTML: container.innerHTML.substring(0, 200),
          isNarrowScreen
        });
        setIsCalculating(false);
        return; 
      }
      if (!a4Container) {
          console.error('calculatePages Error: a4ContainerRef is not available.');
          setIsCalculating(false);
          return;
      }

      // FIX: Use logical A4 dimensions instead of getBoundingClientRect which is affected by zoom transforms
      // const a4Rect = a4Container.getBoundingClientRect();
      // const a4HeightPx = a4Rect.height;
      const a4HeightPx = A4_HEIGHT_MM * MM_TO_PX_FACTOR; // Use logical A4 height in pixels
      const paddingPx = PAGE_PADDING_MM * MM_TO_PX_FACTOR;
      const baseAvailablePageHeightPx = a4HeightPx - (2 * paddingPx) + HEIGHT_BUFFER_PX;

      const sidebarSectionNames = ['Skills', 'Languages', 'Certifications'];
      const isSidebarLayout = selectedLayout === 'sidebar-left' || selectedLayout === 'sidebar-right';
      const isTopbarLayout = selectedLayout.startsWith('topbar-');

      console.log(`A4 height: ${a4HeightPx}px (logical), Padding: ${paddingPx}px, Buffer: ${HEIGHT_BUFFER_PX}px, Base Available Height: ${baseAvailablePageHeightPx}px`);

      let pagesResult = [];
      let currentPageSections = [];
      let currentPageHeight = 0;
      let debugTotalHeight = 0;

      // --- Calculate Fixed Space and Page 1 Height Limit --- 
      let firstPageFixedSpacePx = 0;
      let firstPageAvailableHeightPx = baseAvailablePageHeightPx; // Default to base height

      if (isTopbarLayout) {
          const topbarHeightPx = TOPBAR_HEIGHT_MM * MM_TO_PX_FACTOR; // Topbar height in px
          const bottomPaddingPx = paddingPx; // Use the already calculated padding for bottom
          firstPageFixedSpacePx = topbarHeightPx; // Technically the topbar isn't part of measured content, but represents the offset
          firstPageAvailableHeightPx = a4HeightPx - topbarHeightPx - bottomPaddingPx + HEIGHT_BUFFER_PX; // Correct calculation for topbar page 1
          console.log(`Topbar Layout: TopbarHeight=${topbarHeightPx}px, BottomPadding=${bottomPaddingPx}px, Page 1 Available Height = ${firstPageAvailableHeightPx}px`);
      } else {
          const headerElement = container.querySelector('[data-section="header"]');
          if (headerElement) {
              const headerRect = headerElement.getBoundingClientRect();
              firstPageFixedSpacePx = headerRect.height; // Header height + spacing
              firstPageAvailableHeightPx = baseAvailablePageHeightPx - firstPageFixedSpacePx; // Subtract header from base available height
              console.log(`Header Layout: HeaderHeight=${firstPageFixedSpacePx}px, Page 1 Available Height = ${firstPageAvailableHeightPx}px`);
          }
      }
      // --- End Fixed Space Calculation --- 

      sectionElements.forEach((section, index) => {
        const sectionName = section.getAttribute('data-section');
        const sectionObj = selectedSections.find(s => s.name === sectionName);
        if (!sectionObj) return;

        if (!isTopbarLayout && sectionName === 'header') {
            return; // Skip header element itself for non-topbar
        }

        const rect = section.getBoundingClientRect();
        const sectionHeight = rect.height;
        debugTotalHeight += sectionHeight;
        console.log(`Section ${sectionName}: Measured Height = ${sectionHeight}px`);
        if (sectionHeight <= 0) {
             console.warn(`Section ${sectionName}: Measured height is ${sectionHeight}px.`);
        }

        let effectiveSectionHeight = sectionHeight;
        let isFirstPage = pagesResult.length === 0;
        
        // Determine the correct height limit for the current page
        let availablePageHeightPx = isFirstPage ? firstPageAvailableHeightPx : baseAvailablePageHeightPx;

        if (isSidebarLayout && isFirstPage && sidebarSectionNames.includes(sectionName)) {
            console.log(`Sidebar Page 1: Excluding ${sectionName} from height calc.`);
            currentPageSections.push(sectionObj);
            return;
        }

        const spacing = currentPageSections.length > 0 ? 10 : 0;

        console.log(`Checking fit for ${sectionName}: Current Height=${currentPageHeight}px, Section Height=${effectiveSectionHeight}px, Spacing=${spacing}, Limit=${availablePageHeightPx}px`);
        if (currentPageHeight + spacing + effectiveSectionHeight <= availablePageHeightPx) {
          currentPageSections.push(sectionObj);
          currentPageHeight += spacing + effectiveSectionHeight;
        } else {
          console.log(`Page break before ${sectionName}: Current Height=${currentPageHeight}px + Section Height=${effectiveSectionHeight}px (+ spacing ${spacing}) exceeds limit ${availablePageHeightPx}`);
          pagesResult.push([...currentPageSections]);

          // Start new page
          currentPageSections = [sectionObj];
          currentPageHeight = effectiveSectionHeight; 
          // --- IMPORTANT: Reset page limit calculation for the *new* page being started ---
          // Since a page break just happened, the *next* page limit applies.
          availablePageHeightPx = baseAvailablePageHeightPx; // Pages 2+ always use base height
        }
      });

      if (currentPageSections.length > 0) {
        pagesResult.push([...currentPageSections]);
      }

      if (pagesResult.length === 0) {
         if (firstPageFixedSpacePx > 0) {
             pagesResult = [[]]; 
         } else {
             pagesResult = [[]];
         }
      }

      console.log(`Total measured content height: ${debugTotalHeight}px, created ${pagesResult.length} pages`);
      console.log('Pages distribution:', pagesResult.map(page => page.map(section => section.name).join(', ')));

      const pagesString = JSON.stringify(pagesResult);
      const prevPagesString = JSON.stringify(pages);

      if (pagesString !== prevPagesString) {
        console.log('LivePreview: Updating pages state:', pagesResult);
        setPages(pagesResult);
        if (currentPage >= pagesResult.length) {
          setCurrentPage(Math.max(0, pagesResult.length - 1));
        }
      }

    } catch (error) {
        console.error("Error during page calculation:", error);
    } finally {
      setIsCalculating(false);
    }
  }, [selectedSections, pages, setPages, currentPage, setCurrentPage, formData, isCalculating, selectedLayout]);

  // Enhance the render complete handler to ensure content is fully rendered
  const handleRenderComplete = useCallback(() => {
    // Use requestAnimationFrame + setTimeout for better timing on mobile
    if (isNarrowScreen) {
      // On mobile, use multiple frame delays to ensure stability
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (contentRef.current) {
              void contentRef.current.offsetHeight;
            }
            console.log('LivePreview: handleRenderComplete (mobile) triggered pagination');
            calculatePages();
          }, 400);
        });
      });
    } else {
      // Desktop - use original timing
      setTimeout(() => {
        if (contentRef.current) {
          void contentRef.current.offsetHeight;
        }
        console.log('LivePreview: handleRenderComplete (desktop) triggered pagination');
        calculatePages();
      }, 300);
    }
  }, [calculatePages, isNarrowScreen]);

  // Add a special useEffect for typography and layout changes
  useEffect(() => {
    // When typography or layout changes, wait for rendering and recalculate
    if (!isInitialMount.current) {
      const timer = setTimeout(() => {
        // Force reflow before calculation
        if (contentRef.current) {
          void contentRef.current.offsetHeight;
        }
        calculatePages();
      }, 500); // Longer timeout for style changes
      
      return () => clearTimeout(timer);
    }
  }, [typographySettings, selectedLayout, activeDesign, calculatePages]);

  // Re-calculate pages when content changes
  useEffect(() => {
    // Mark initial mount as complete, but still allow calculation if there's data
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // Don't return early - allow calculation to proceed if there's data
    }

    // Use longer timeout on mobile devices for DOM stability
    const timeoutDelay = isNarrowScreen ? 500 : 150;
    
    // Debounce the calculation to reduce frequency
    const timer = setTimeout(() => {
      // Add clear checks to prevent unnecessary calculations
      if (!isCalculating && contentRef.current && selectedSections.length > 0) {
        console.log('LivePreview: Attempting pagination calculation', {
          isNarrowScreen,
          selectedSectionsLength: selectedSections.length,
          hasContentRef: !!contentRef.current,
          timeoutDelay
        });
        calculatePages();
      }
    }, timeoutDelay);

    return () => clearTimeout(timer);

    // Add formData to the dependency array
  }, [calculatePages, selectedSections, typographySettings, selectedLayout, activeDesign, formData, isNarrowScreen]);

  // Helper function to calculate distance between two touch points
  const getTouchDistance = (touches) => {
    if (touches.length < 2) return null;
    
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };
  
  // Helper function to calculate midpoint between two touch points
  const getTouchMidpoint = (touches) => {
    if (touches.length < 2) return null;
    
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2
    };
  };

  // Handle mouse and touch interactions for panning and zooming the preview
  useEffect(() => {
    const a4Container = a4ContainerRef.current;
    if (!a4Container) return;
    
    const handleMouseDown = (e) => {
      // Don't hide hint on mouse interaction for mobile users
      // (they should use touch gestures)
      
      // Enable dragging with any mouse button (primarily left button)
      isDragging.current = true;
      lastPosition.current = { x: e.clientX, y: e.clientY };
      a4Container.style.cursor = 'grabbing';
      e.preventDefault();
    };
    
    const handleMouseMove = (e) => {
      if (!isDragging.current) return;
      
      const deltaX = e.clientX - lastPosition.current.x;
      const deltaY = e.clientY - lastPosition.current.y;
      
      setPosition(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      
      lastPosition.current = { x: e.clientX, y: e.clientY };
    };
    
    const handleMouseUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        a4Container.style.cursor = 'grab';
      }
    };
    
    // Touch event handlers for mobile
    const handleTouchStart = (e) => {
      // Only hide hint after significant interaction (pinch or multiple touches)
      if (!hasInteracted && (e.touches.length > 1)) {
        setHasInteracted(true);
        setShowGestureHint(false);
      }
      
      if (e.touches.length === 1) {
        // Single touch - pan mode
        isDragging.current = true;
        lastPosition.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      } 
      else if (e.touches.length === 2) {
        // Two touches - pinch zoom mode
        isDragging.current = false;
        
        // Store initial pinch distance and current scale
        lastPinchDistance.current = getTouchDistance(e.touches);
        pinchStartScale.current = scale;
        
        // Store the midpoint between the touches
        pinchMidpoint.current = getTouchMidpoint(e.touches);
      }
      // Don't prevent default for touch events to allow scrolling
    };
    
    const handleTouchMove = (e) => {
      if (e.touches.length === 1 && isDragging.current) {
        // Single touch - panning
        const deltaX = e.touches[0].clientX - lastPosition.current.x;
        const deltaY = e.touches[0].clientY - lastPosition.current.y;
        
        setPosition(prev => ({
          x: prev.x + deltaX,
          y: prev.y + deltaY
        }));
        
        lastPosition.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        // Prevent default to avoid page scrolling during pan
        e.preventDefault();
      } 
      else if (e.touches.length === 2 && lastPinchDistance.current !== null) {
        // Two touches - pinch zooming
        const currentDistance = getTouchDistance(e.touches);
        const scaleChange = currentDistance / lastPinchDistance.current;
        // Calculate new scale based on the pinch
        const newScale = Math.min(
          MAX_SCALE, 
          Math.max(MIN_SCALE, pinchStartScale.current * scaleChange)
        );
        // Get current midpoint
        const currentMidpoint = getTouchMidpoint(e.touches);
        // Update the scale
        setScale(newScale);
        // Prevent default to avoid page zooming
        e.preventDefault();
      }
    };
    
    const handleTouchEnd = (e) => {
      if (e.touches.length < 2) {
        // Reset pinch zoom tracking when fewer than 2 fingers
        lastPinchDistance.current = null;
      }
      if (e.touches.length === 0) {
        // No more touches - reset dragging state
        isDragging.current = false;
      }
    };
    
    // Set initial cursor style
    a4Container.style.cursor = 'grab';
    
    // Add event listeners
    a4Container.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    a4Container.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      // Remove event listeners
      a4Container.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      a4Container.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [scale, hasInteracted]); // Add hasInteracted to dependencies

  // Reset position when scale changes to default
  useEffect(() => {
    if (scale === defaultScale) {
      setPosition({ x: 0, y: 0 });
    }
  }, [scale, defaultScale]);

  // Mobile page navigation handlers
  const handlePreviousPage = useCallback(() => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage, setCurrentPage]);

  const handleNextPage = useCallback(() => {
    if (currentPage < pages.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, pages.length, setCurrentPage]);

  // Effect for initial mount
  useEffect(() => {
    // Initial mount setup is now handled in the main pagination effect above
    // This effect is kept for any future initial setup needs
  }, []);

  // Determine which sections to render on the current page
  const sectionsForCurrentPage = pages[currentPage] || [];
  const sectionsForHiddenCalculation = selectedSections; // Render all selected sections for measurement

  // --- DEBUG LOG --- Add logging before rendering LayoutResolver
  console.log(`LivePreview rendering: Page ${currentPage + 1}/${pages.length}. Sections for page:`, sectionsForCurrentPage.map(s => s.name));
  if (sectionsForCurrentPage.length === 0 && selectedSections.length > 0 && pages.length > 0) {
    console.warn('LivePreview: sectionsForCurrentPage is empty, but sections ARE selected and pages exist!');
  }
  // --- END DEBUG LOG ---

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      position: 'relative',
      background: tokens.colors.background.gradient,
    }}>
      <Box sx={{ 
        flex: 1, 
        overflow: 'hidden', 
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        {/* Preview container with scroll capability */}
        <Box
          ref={previewContainerRef}
          sx={{
            width: '100%',
            height: '100%',
            position: 'relative',
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: tokens.components.preview.padding,
          }}
        >
          {/* A4 container with transform for zoom and pan */}
          <Box
            ref={a4ContainerRef}
            id="pdf-content"
            sx={{
              width: `${A4_WIDTH_MM}mm`,
              height: `${A4_HEIGHT_MM}mm`,
              minWidth: `${A4_WIDTH_MM}mm`,
              minHeight: `${A4_HEIGHT_MM}mm`,
              bgcolor: currentColors?.background || '#ffffff',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              overflow: 'hidden',
              position: 'relative',
              transformOrigin: 'center center',
              transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
              transition: isDragging.current ? 'none' : 'transform 0.1s ease-out',
              '& *': {
                maxWidth: '100%',
                boxSizing: 'border-box',
              }
            }}
          >
            {/* Render the actual CV content on TOP */}
            <Box sx={{ position: 'relative', zIndex: 1, backgroundColor: 'transparent' }}>
              <LayoutResolver
                selectedLayout={selectedLayout}
                formData={formData}
                selectedSections={sectionsForCurrentPage}
                design={activeDesign}
                typographySettings={typographySettings}
                currentPage={currentPage}
                totalPages={pages.length}
                measureMode={false}
                selectedBackground={selectedBackgroundId}
                activeBackgroundComponent={ActiveBackgroundComponent}
                activeStyle={contextActiveStyle}
                isPdfExport={false}
                selectedSpecialEffectId={selectedSpecialEffectId}
                activeBarStyle={activeBarStyle}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      
      {/* Mobile Gesture Hint */}
      {isNarrowScreen && (
        <Fade in={showGestureHint} timeout={800}>
          <Box
            sx={{
              position: 'absolute',
              top: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 0.8,
              backgroundColor: 'rgba(255, 255, 255, 0.92)',
              color: 'rgba(0, 0, 0, 0.7)',
              padding: '6px 14px',
              borderRadius: '16px',
              fontSize: '0.7rem',
              fontWeight: 400,
              backdropFilter: 'blur(12px)',
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              border: '1px solid rgba(255, 255, 255, 0.8)',
              zIndex: 999,
              pointerEvents: 'none',
              opacity: 0.85,
            }}
          >
            <TouchApp sx={{ fontSize: '0.9rem', opacity: 0.6, color: tokens.colors.primary.main }} />
                         <Typography variant="caption" sx={{ 
               color: 'rgba(0, 0, 0, 0.65)', 
               fontSize: '0.68rem',
               letterSpacing: '0.2px',
               fontWeight: 400
             }}>
               {t('mobile.gestureHint')}
             </Typography>
          </Box>
        </Fade>
      )}

      {/* Mobile Page Navigation Arrows */}
      {isNarrowScreen && pages.length > 1 && (
        <>
          {/* Previous Page Arrow */}
          <Fade in={currentPage > 0} timeout={300}>
            <Box
              sx={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 999,
              }}
            >
              <IconButton
                onClick={handlePreviousPage}
                disabled={currentPage === 0}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: tokens.colors.primary.main,
                  width: 40,
                  height: 40,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  backdropFilter: 'blur(4px)',
                  border: `1px solid ${tokens.colors.borders.light}`,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    transform: 'scale(1.05)',
                  },
                  '&:disabled': {
                    opacity: 0.3,
                    pointerEvents: 'none',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <ChevronLeft />
              </IconButton>
            </Box>
          </Fade>

          {/* Next Page Arrow */}
          <Fade in={currentPage < pages.length - 1} timeout={300}>
            <Box
              sx={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 999,
              }}
            >
              <IconButton
                onClick={handleNextPage}
                disabled={currentPage === pages.length - 1}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: tokens.colors.primary.main,
                  width: 40,
                  height: 40,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  backdropFilter: 'blur(4px)',
                  border: `1px solid ${tokens.colors.borders.light}`,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    transform: 'scale(1.05)',
                  },
                  '&:disabled': {
                    opacity: 0.3,
                    pointerEvents: 'none',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <ChevronRight />
              </IconButton>
            </Box>
          </Fade>

          {/* Page Indicator */}
          <Box
            sx={{
              position: 'absolute',
              bottom: '12px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '0.75rem',
              fontWeight: 500,
              backdropFilter: 'blur(8px)',
              zIndex: 999,
              pointerEvents: 'none',
            }}
          >
            {currentPage + 1} / {pages.length}
          </Box>
        </>
      )}
      
      {/* Move hidden measurement container OUTSIDE the transformed a4Container */}
      <Box
        ref={contentRef}
        sx={{
          position: 'absolute',
          left: '-9999px',
          top: '-9999px',
          width: `${A4_WIDTH_MM}mm`,
          pointerEvents: 'none',
          fontFamily: theme.typography.fontFamily, 
          fontSize: theme.typography.fontSize,   
          lineHeight: theme.typography.lineHeight, 
          boxSizing: 'border-box',
          visibility: 'visible', 
          zIndex: -1, 
        }}
      >
        {/* FIX: Render ALL selected sections here using LayoutResolver for measurement */}
        <LayoutResolver
            selectedLayout={selectedLayout} // Use current layout
            formData={formData}
            selectedSections={sectionsForHiddenCalculation} // Render ALL sections
            design={activeDesign}
            typographySettings={typographySettings}
            currentPage={0} // Dummy page info for measurement
            totalPages={1}
            measureMode={true} // IMPORTANT: Indicate measurement mode
            activeStyle={contextActiveStyle} // Pass activeStyle from context
            selectedBackground={selectedBackgroundId} // Pass selectedBackground
            activeBackgroundComponent={ActiveBackgroundComponent} // Pass background component
            selectedSpecialEffectId={selectedSpecialEffectId}
            onRenderComplete={() => {
              console.log('Measurement LayoutResolver render complete');
              handleRenderComplete();
            }} // Trigger calculation when measurement layout renders
            activeBarStyle={activeBarStyle}
        />
      </Box>
      
      {/* Page controls */}
      <PageControls
        scale={scale}
        setScale={setScale}
        position={position}
        setPosition={setPosition}
        isNarrowScreen={isNarrowScreen}
        defaultScale={defaultScale}
      />
      
      {/* Clear All Button - Only show on desktop */}
      {!isNarrowScreen && (
        <ClearAllData
          onClear={handleClearAll}
        />
      )}
    </Box>
  );
}

export default LivePreview;
